import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SpinDecision from './SpinDecision';
import SlotGuide from './SlotGuide';
import {
  CONTRACTS,
  MAX_HP,
  MAX_TOTAL_SPINS,
  REEL_STRIP,
  STARTING_SPINS,
  SYMBOLS,
  TARGET_SCORE,
  continueRun,
  createGame,
  getContractOptions,
  getSpinPreview,
  lockSpin,
  nudge,
  spin,
  startRun,
  takeContract,
} from './game';
import './CursedSlotMachine.css';

const STORAGE_KEY = 'slashRoll.cursedSlots.v2';
const PHASES = ['intro', 'ready', 'spun', 'result', 'contract', 'won', 'lost'];

const validSymbol = (symbol) => typeof symbol === 'string' && !!SYMBOLS[symbol];

const loadGame = () => {
  try {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY));
    if (
      saved &&
      PHASES.includes(saved.phase) &&
      Number.isInteger(saved.spinNumber) &&
      saved.spinNumber >= 1 &&
      saved.spinNumber <= MAX_TOTAL_SPINS &&
      Number.isInteger(saved.totalSpins) &&
      saved.totalSpins >= STARTING_SPINS &&
      saved.totalSpins <= MAX_TOTAL_SPINS &&
      Number.isInteger(saved.score) &&
      saved.score >= 0 &&
      Number.isInteger(saved.hp) &&
      saved.hp >= 0 &&
      saved.hp <= MAX_HP &&
      Number.isInteger(saved.nudges) &&
      saved.nudges >= 0 &&
      saved.nudges <= 3 &&
      Array.isArray(saved.contracts) &&
      saved.contracts.every((id) => CONTRACTS[id]) &&
      Array.isArray(saved.history) &&
      (saved.phase !== 'spun' ||
        (Array.isArray(saved.reels) &&
          saved.reels.length === 3 &&
          saved.reels.every(validSymbol))) &&
      (!['result', 'won', 'lost'].includes(saved.phase) || saved.result)
    )
      return { ...createGame(), ...saved };
  } catch {
    // An old, damaged, or unavailable save should not block a new run.
  }
  return createGame();
};

const getDealerLine = (game, preview) => {
  if (game.phase === 'intro')
    return 'Every pull pays something. Sometimes it pays me.';
  if (game.phase === 'contract')
    return 'A blessing on the front. Teeth on the back.';
  if (game.phase === 'won')
    return 'You broke the house. Enjoy the silence while it lasts.';
  if (game.phase === 'lost')
    return game.hp === 0
      ? 'The machine has collected its debt.'
      : 'Eight chances became none.';
  if (game.phase === 'result' && game.result) {
    if (game.result.event === 'jackpot')
      return 'The house will remember that sound.';
    if (game.result.event === 'nearMiss')
      return 'One symbol away. Cruel, is it not?';
    if (game.result.event === 'bonus')
      return 'Another spin, courtesy of your terrible judgment.';
    if (game.result.event === 'doom') return 'Skulls always collect.';
    return game.result.payout >= 25
      ? 'A respectable theft.'
      : 'Barely enough to keep playing.';
  }
  if (game.phase === 'spun' && preview)
    return preview.hpLoss > 0
      ? 'I would change that, if I were you.'
      : 'Perhaps the machine likes you.';
  if (game.contracts.length > 0)
    return 'Your contracts are awake. Pull carefully.';
  return 'Three reels. One bad decision at a time.';
};

const Dealer = ({ line, dangerous }) => (
  <div className={`slotDealer ${dangerous ? 'slotDealerDanger' : ''}`}>
    <div className="slotDealerPortrait" aria-hidden="true">
      <span className="slotDealerHorn slotDealerHornLeft" />
      <span className="slotDealerHorn slotDealerHornRight" />
      <span className="slotDealerEye slotDealerEyeLeft" />
      <span className="slotDealerEye slotDealerEyeRight" />
      <span className="slotDealerSmile" />
    </div>
    <p>
      <span>THE DEALER</span>
      {line}
    </p>
  </div>
);

const CursedSlotMachine = () => {
  const [game, setGame] = useState(loadGame);
  const [showGuide, setShowGuide] = useState(false);
  const preview = getSpinPreview(game);
  const reduceMotion = !!window.matchMedia?.('(prefers-reduced-motion: reduce)')
    .matches;
  const contractClasses = game.contracts
    .map((id) => `slotTheme-${CONTRACTS[id].accent}`)
    .join(' ');

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(game));
    } catch {
      // Play remains available when browser storage is disabled.
    }
  }, [game]);

  const randomSymbol = () =>
    REEL_STRIP[Math.floor(Math.random() * REEL_STRIP.length)];
  const pullLever = () =>
    setGame((current) =>
      spin(current, [randomSymbol(), randomSymbol(), randomSymbol()])
    );
  const reroll = (index) =>
    setGame((current) => nudge(current, index, randomSymbol()));

  return (
    <main
      className={`slotPage ${contractClasses} ${
        game.phase !== 'intro' ? 'slotPlaying' : ''
      } ${game.phase === 'contract' ? 'slotChoosingContract' : ''}`}
    >
      <div className="slotContractAura" aria-hidden="true">
        {game.contracts.map((id) => (
          <span key={id} className={`slotAura-${CONTRACTS[id].accent}`} />
        ))}
      </div>
      <header className="slotTopbar">
        <Link to="/single-player">← Games</Link>
        <span>SLASH ROLL / SOLO</span>
        <button
          className="slotInfoButton"
          onClick={() => setShowGuide(true)}
          aria-haspopup="dialog"
          aria-label="Symbol and scoring guide"
          title="Symbols & scoring"
        >
          <span aria-hidden="true">ⓘ</span>
          <span className="slotInfoLabel"> Symbols & scoring</span>
        </button>
        <Link to="/">Home</Link>
      </header>

      <div className="slotLayout">
        {game.phase !== 'intro' && (
          <section
            className="slotSidePanel slotRunStatus"
            aria-label="Run status"
          >
            <span className="slotEyebrow">THIS RUN</span>
            <div className="slotStatusCell">
              <div className="slotStat">
                <span>HP</span>
                <strong>
                  {game.hp} / {MAX_HP}
                </strong>
              </div>
              <div
                className="slotProgress"
                role="progressbar"
                aria-label="HP"
                aria-valuemin={0}
                aria-valuemax={MAX_HP}
                aria-valuenow={game.hp}
              >
                <span style={{ width: `${(game.hp / MAX_HP) * 100}%` }} />
              </div>
            </div>
            <div className="slotStatusCell">
              <div className="slotStat">
                <span>Spin</span>
                <strong>
                  {game.spinNumber} / {game.totalSpins}
                </strong>
              </div>
            </div>
            <div className="slotStatusCell">
              <div className="slotStat">
                <span>Score</span>
                <strong>
                  {game.score} / {TARGET_SCORE}
                </strong>
              </div>
              <div
                className="slotProgress"
                role="progressbar"
                aria-label="Score"
                aria-valuemin={0}
                aria-valuemax={TARGET_SCORE}
                aria-valuenow={Math.min(game.score, TARGET_SCORE)}
              >
                <span
                  style={{
                    width: `${Math.min(
                      100,
                      (game.score / TARGET_SCORE) * 100
                    )}%`,
                  }}
                />
              </div>
            </div>
            <div className="slotStatusCell">
              <div className="slotStat">
                <span>Nudges</span>
                <strong>{game.nudges}</strong>
              </div>
            </div>
          </section>
        )}
        <div className="slotMain">
          {game.phase === 'intro' && (
            <div className="slotHeading">
              <span className="slotEyebrow">
                A GAME OF SYMBOLS AND BAD BARGAINS
              </span>
              <h1>The Cursed Slot Machine</h1>
              <p>
                Build a payout from three reels. Chase combinations, bend one
                symbol with a nudge, and sign contracts that physically change
                the machine.
              </p>
            </div>
          )}

          <Dealer
            line={getDealerLine(game, preview)}
            dangerous={(preview && preview.hpLoss > 0) || game.phase === 'lost'}
          />

          {game.phase === 'intro' && (
            <section className="slotPanel">
              <span className="slotEyebrow">HOW TO PLAY</span>
              <h2>Beat the house before the pulls run out.</h2>
              <p>
                Reach {TARGET_SCORE} points in {STARTING_SPINS} spins. Three
                matching symbols trigger a jackpot and add a bonus spin. Two
                Sevens or Wilds create a lucrative near miss. Crown lines can
                also extend the run.
              </p>
              <div className="slotSymbolGuide">
                {['7', 'wild', 'crown', 'eye', 'skull'].map((id) => (
                  <div key={id} className={`slotGuide-${id}`}>
                    <strong>{SYMBOLS[id].glyph}</strong>
                    <span>{SYMBOLS[id].name}</span>
                  </div>
                ))}
              </div>
              <p>
                A payout below 10 costs HP, and Skull lines bite harder. You
                begin with two nudges. Contracts arrive after spins 2, 4, and 6.
              </p>
              <button className="slotPrimary" onClick={() => setGame(startRun)}>
                Pull up a chair →
              </button>
              {(game.bestScore > 0 || game.victories > 0) && (
                <p className="slotRecord">
                  Best score: {game.bestScore} · Wins: {game.victories}
                </p>
              )}
            </section>
          )}

          {game.phase === 'ready' && (
            <section className="slotPanel slotReady">
              <span className="slotEyebrow">
                SPIN {game.spinNumber} / {game.totalSpins}
              </span>
              <h2>Feed the machine another possibility</h2>
              <div
                className="slotMachineWindow slotMachineIdle"
                aria-hidden="true"
              >
                <div className="slotPayline" />
                <div className="slotReels">
                  <div className="slotReel">?</div>
                  <div className="slotReel">?</div>
                  <div className="slotReel">?</div>
                </div>
              </div>
              <p>
                Three of a kind wins a jackpot. A matching pair pays +12. Each
                symbol also contributes its value.
              </p>
              <button className="slotPrimary slotLever" onClick={pullLever}>
                <span className="slotLeverSparks" aria-hidden="true">
                  {Array.from({ length: 8 }, (_, index) => (
                    <i key={index} style={{ '--spark': index }} />
                  ))}
                </span>
                <span className="slotLeverHandle" aria-hidden="true" />
                Pull the lever
              </button>
            </section>
          )}

          {game.phase === 'spun' && preview && (
            <SpinDecision
              key={game.spinVersion}
              game={game}
              preview={preview}
              onNudge={reroll}
              onLock={() => setGame(lockSpin)}
            />
          )}

          {game.phase === 'contract' && (
            <section className="slotPanel slotContractPanel">
              <span className="slotEyebrow">BEFORE SPIN {game.spinNumber}</span>
              <h2>Choose your contract</h2>
              <p>
                One blessing. One curse. Both last the run. Signing restores a
                nudge (max 3).
              </p>
              <div className="slotContractGrid">
                {getContractOptions(game).map((id) => (
                  <button
                    className={`slotContractChoice slotContract-${CONTRACTS[id].accent}`}
                    key={id}
                    onClick={() =>
                      setGame((current) => takeContract(current, id))
                    }
                  >
                    <span className="slotContractSeal">✦</span>
                    <span className="slotEyebrow">CURSED CONTRACT</span>
                    <strong>{CONTRACTS[id].name}</strong>
                    <span className="slotBoon">✦ {CONTRACTS[id].boon}</span>
                    <span className="slotCurse">☠ {CONTRACTS[id].curse}</span>
                    <span className="slotPick">Sign in blood →</span>
                  </button>
                ))}
              </div>
            </section>
          )}

          {['result', 'won', 'lost'].includes(game.phase) && game.result && (
            <motion.section
              className={`slotPanel slotResult slotEvent-${game.result.event} ${
                game.phase === 'won'
                  ? 'slotWon'
                  : game.phase === 'lost'
                  ? 'slotLost'
                  : ''
              }`}
              initial={
                reduceMotion ? false : { opacity: 0, y: 24, scale: 0.96 }
              }
              animate={reduceMotion ? {} : { opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.35 }}
            >
              <span className="slotEyebrow">
                SPIN {game.spinNumber} / {game.totalSpins}
              </span>
              <h2>
                {game.phase === 'won'
                  ? 'You broke the house!'
                  : game.phase === 'lost'
                  ? 'The house collects'
                  : game.result.event === 'jackpot'
                  ? 'Jackpot secured!'
                  : 'Payout locked in'}
              </h2>
              <div className="slotResultSymbols">
                {game.result.reels.map((id, index) => (
                  <span className={`slotSymbol-${id}`} key={`${id}-${index}`}>
                    {SYMBOLS[id].glyph}
                  </span>
                ))}
              </div>
              <p className="slotResultPayout">
                +{game.result.payout} points
                {game.result.hpLoss > 0 ? ` · −${game.result.hpLoss} HP` : ''}
              </p>
              {game.result.bonusSpins > 0 && (
                <p className="slotBonusNotice">
                  Bonus spin awarded. This run now has {game.totalSpins} spins.
                </p>
              )}
              <p>
                Total: {game.score} / {TARGET_SCORE} · HP: {game.hp} / {MAX_HP}
              </p>
              {game.phase === 'result' && (
                <button
                  className="slotPrimary"
                  onClick={() => setGame(continueRun)}
                >
                  {[2, 4, 6].includes(game.spinNumber)
                    ? 'Meet the dealer →'
                    : 'Next spin →'}
                </button>
              )}
              {game.phase === 'lost' && (
                <p>
                  {game.hp === 0
                    ? 'Your HP reached zero.'
                    : 'The final spin ended before you reached the target.'}
                </p>
              )}
              {['won', 'lost'].includes(game.phase) && (
                <>
                  <p>
                    Best score: {game.bestScore} · Wins: {game.victories}
                  </p>
                  <button
                    className="slotPrimary"
                    onClick={() => setGame(startRun)}
                  >
                    Play again →
                  </button>
                </>
              )}
            </motion.section>
          )}
        </div>

        {game.phase !== 'intro' && (
          <aside className="slotSidebar">
            <div className="slotSidePanel">
              <span className="slotEyebrow">SIGNED CONTRACTS</span>
              {game.contracts.length === 0 ? (
                <p>None yet. The first offer arrives after spin 2.</p>
              ) : (
                game.contracts.map((id) => (
                  <div
                    className={`slotSigned slotSigned-${CONTRACTS[id].accent}`}
                    key={id}
                  >
                    <strong>{CONTRACTS[id].name}</strong>
                    <span className="slotBoon">{CONTRACTS[id].boon}</span>
                    <span className="slotCurse">{CONTRACTS[id].curse}</span>
                  </div>
                ))
              )}
            </div>
            {game.history.length > 0 && (
              <div className="slotSidePanel">
                <span className="slotEyebrow">LAST SPINS</span>
                {game.history.map((result, index) => (
                  <div
                    className="slotHistory"
                    key={`${game.spinNumber}-${index}`}
                  >
                    <span>
                      {result.reels.map((id) => SYMBOLS[id].glyph).join(' ')}
                    </span>
                    <strong>+{result.payout}</strong>
                  </div>
                ))}
              </div>
            )}
          </aside>
        )}
      </div>
      {showGuide && (
        <SlotGuide
          contracts={game.contracts}
          onClose={() => setShowGuide(false)}
        />
      )}
    </main>
  );
};

export default CursedSlotMachine;
